import type { UseDisclosureReturn } from '@chakra-ui/react-v2';
import { Form, Formik } from 'formik';

import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverline,
  DrawerCloseButton,
  DrawerBody,
  VStack,
  HStack,
  Divider,
  Heading,
  DrawerFooter,
  DrawerButtonGroup,
  Button,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@frontend/legacy-components';

import {
  CaseTypeField,
  DescriptionField,
  EmailField,
  HiddenFields,
  PriorityField,
  SubjectField,
  SupportBanner,
} from './components';
import { useSupportFormInternals } from './hooks';

export type SupportDrawerProps = Pick<
  UseDisclosureReturn,
  'isOpen' | 'onClose'
>;

export const SupportDrawer = (props: SupportDrawerProps) => {
  const { onClose } = props;

  const {
    supportFormRef,
    submitSupportForm,
    supportFormInitialValues,
    supportFormValidationSchema,
  } = useSupportFormInternals(onClose);

  return (
    <Drawer {...props}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerOverline>Help</DrawerOverline>
          Contact Support
          <DrawerCloseButton />
        </DrawerHeader>
        <Formik
          initialValues={supportFormInitialValues}
          onSubmit={submitSupportForm}
          validationSchema={supportFormValidationSchema}
        >
          {({ resetForm, submitForm, isSubmitting, validateForm, isValid }) => {
            const submitIsDisabled = !isValid;

            return (
              <>
                <DrawerBody p={0}>
                  <VStack alignItems="stretch" width="100%">
                    <SupportBanner />
                    <Form ref={supportFormRef} style={{ width: '100%' }}>
                      <HiddenFields />
                      <VStack width="100%" p={2} spacing={3}>
                        <HStack width="100%">
                          <CaseTypeField />
                          <PriorityField />
                        </HStack>
                        <Divider />
                        <VStack width="100%" alignItems="stretch">
                          <Heading size="lg">Message</Heading>
                          <EmailField
                            isEnabled={!supportFormInitialValues.email}
                          />
                          <SubjectField />
                          <DescriptionField />
                        </VStack>
                      </VStack>
                    </Form>
                  </VStack>
                </DrawerBody>
                <DrawerFooter>
                  <VStack alignItems="flex-end">
                    <Alert status="warning">
                      <AlertIcon />
                      <AlertDescription>
                        {' '}
                        Support conversations are processed using tools outside
                        of the Paperless Parts FedRAMP-authorized boundary.
                        Please do not include CUI or ITAR in your Support
                        submission. You may reference quote #s, Part #s, or any
                        other data stored in your Paperless Parts account for
                        the Support team to review.
                      </AlertDescription>
                    </Alert>

                    <DrawerButtonGroup>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          resetForm();
                          onClose();
                        }}
                      >
                        Cancel
                      </Button>
                      <Tooltip
                        label={
                          submitIsDisabled
                            ? 'Please fix the errors highlighted above'
                            : undefined
                        }
                      >
                        <Button
                          isLoading={isSubmitting}
                          onClick={() => {
                            validateForm().then(() => {
                              submitForm();
                            });
                          }}
                          isDisabled={submitIsDisabled}
                        >
                          Send message
                        </Button>
                      </Tooltip>
                    </DrawerButtonGroup>
                  </VStack>
                </DrawerFooter>
              </>
            );
          }}
        </Formik>
      </DrawerContent>
    </Drawer>
  );
};
